import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getDocuments, uploadDocument, deleteDocument, downloadDocument } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Plus, FileText, Search, Trash2, Download, Upload, Folder,
  Filter, LayoutGrid, List, SortAsc, SortDesc, File, Image, FileSpreadsheet
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const CATEGORIES = [
  { value: 'marketing', label: 'Marketing', color: 'bg-blue-100 text-blue-700', icon: Image },
  { value: 'normativas', label: 'Normativas', color: 'bg-amber-100 text-amber-700', icon: FileText },
  { value: 'informativos', label: 'Informativos', color: 'bg-emerald-100 text-emerald-700', icon: File },
  { value: 'finanzas', label: 'Finanzas', color: 'bg-purple-100 text-purple-700', icon: FileSpreadsheet },
  { value: 'actas', label: 'Actas', color: 'bg-rose-100 text-rose-700', icon: FileText },
  { value: 'contratos', label: 'Contratos', color: 'bg-indigo-100 text-indigo-700', icon: FileText },
  { value: 'otros', label: 'Otros', color: 'bg-stone-100 text-stone-700', icon: File }
];

export default function Documents() {
  const { hasPermission } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [sortBy, setSortBy] = useState('date_desc');
  const [showUpload, setShowUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({ name: '', category: 'informativos', description: '' });

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    try {
      const res = await getDocuments();
      setDocuments(res.data);
    } catch (err) { toast.error('Error al cargar documentos'); }
    finally { setLoading(false); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (!uploadForm.name) setUploadForm({ ...uploadForm, name: file.name.split('.')[0] });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadForm.name) return toast.error('Selecciona un archivo y nombre');
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      await uploadDocument(formData, uploadForm);
      toast.success('Documento subido');
      setShowUpload(false);
      setSelectedFile(null);
      setUploadForm({ name: '', category: 'informativos', description: '' });
      fetchDocuments();
    } catch (err) { toast.error('Error al subir documento'); }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await downloadDocument(doc.id);
      const { file_data, file_type, name } = res.data;
      if (file_data) {
        const byteChars = atob(file_data);
        const byteNums = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
        const blob = new Blob([new Uint8Array(byteNums)], { type: file_type || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = name;
        document.body.appendChild(a); a.click();
        window.URL.revokeObjectURL(url); document.body.removeChild(a);
      } else { toast.error('Archivo no disponible'); }
    } catch (err) { toast.error('Error al descargar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    try {
      await deleteDocument(id);
      toast.success('Documento eliminado');
      fetchDocuments();
    } catch (err) { toast.error('Error al eliminar'); }
  };

  const getCat = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[CATEGORIES.length - 1];

  const filtered = documents
    .filter(d => {
      const matchSearch = d.name?.toLowerCase().includes(searchTerm.toLowerCase()) || d.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchCat = filterCategory === 'all' || d.category === filterCategory;
      return matchSearch && matchCat;
    })
    .sort((a, b) => {
      if (sortBy === 'date_desc') return (b.created_at || '').localeCompare(a.created_at || '');
      if (sortBy === 'date_asc') return (a.created_at || '').localeCompare(b.created_at || '');
      if (sortBy === 'name_asc') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'name_desc') return (b.name || '').localeCompare(a.name || '');
      return 0;
    });

  const canEdit = hasPermission('documentos', 'editar');

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div data-testid="documents-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Gestor Documental</h1>
          <p className="text-stone-500 text-sm">{documents.length} documentos</p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowUpload(true)} data-testid="upload-doc-btn">
            <Upload className="w-4 h-4 mr-2" /> Subir Documento
          </Button>
        )}
      </div>

      {/* Category Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {CATEGORIES.map(cat => {
          const count = documents.filter(d => d.category === cat.value).length;
          const Icon = cat.icon;
          return (
            <Card
              key={cat.value}
              className={`cursor-pointer transition-all hover:shadow-md ${filterCategory === cat.value ? 'ring-2 ring-emerald-500' : ''}`}
              onClick={() => setFilterCategory(filterCategory === cat.value ? 'all' : cat.value)}
              data-testid={`cat-${cat.value}`}
            >
              <CardContent className="p-3 text-center">
                <Icon className={`w-5 h-5 mx-auto mb-1 ${cat.color.split(' ')[1]}`} />
                <p className="text-lg font-bold text-stone-800">{count}</p>
                <p className="text-xs text-stone-500">{cat.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Toolbar */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="relative flex-1 sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input placeholder="Buscar documentos..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="documents-search" />
            </div>
            <div className="flex gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[160px]" data-testid="sort-select">
                  <SortAsc className="w-4 h-4 mr-1" /><SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date_desc">Más reciente</SelectItem>
                  <SelectItem value="date_asc">Más antiguo</SelectItem>
                  <SelectItem value="name_asc">Nombre A-Z</SelectItem>
                  <SelectItem value="name_desc">Nombre Z-A</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex border rounded-md">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-stone-100' : ''}`}
                  data-testid="view-list"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-stone-100' : ''}`}
                  data-testid="view-grid"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-stone-400">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No se encontraron documentos</p>
            </div>
          ) : viewMode === 'list' ? (
            <div className="space-y-2">
              {filtered.map(doc => {
                const cat = getCat(doc.category);
                const CatIcon = cat.icon;
                return (
                  <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-stone-50 transition-colors" data-testid={`doc-${doc.id}`}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg ${cat.color.split(' ')[0]}`}>
                        <CatIcon className={`w-4 h-4 ${cat.color.split(' ')[1]}`} />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-medium text-sm text-stone-800 truncate">{doc.name}</h4>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge className={`text-xs ${cat.color}`}>{cat.label}</Badge>
                          <span className="text-xs text-stone-400">
                            {doc.created_at ? (() => { try { return format(parseISO(doc.created_at), "d MMM yyyy", { locale: es }); } catch { return ''; } })() : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      <Button variant="outline" size="sm" onClick={() => handleDownload(doc)} data-testid={`download-doc-${doc.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                      {canEdit && (
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filtered.map(doc => {
                const cat = getCat(doc.category);
                const CatIcon = cat.icon;
                return (
                  <Card key={doc.id} className="hover:shadow-md transition-shadow" data-testid={`doc-grid-${doc.id}`}>
                    <CardContent className="p-4 text-center">
                      <div className={`w-12 h-12 rounded-lg mx-auto mb-3 flex items-center justify-center ${cat.color.split(' ')[0]}`}>
                        <CatIcon className={`w-6 h-6 ${cat.color.split(' ')[1]}`} />
                      </div>
                      <h4 className="font-medium text-sm text-stone-800 truncate">{doc.name}</h4>
                      <Badge className={`text-xs mt-2 ${cat.color}`}>{cat.label}</Badge>
                      <p className="text-xs text-stone-400 mt-1">
                        {doc.created_at ? (() => { try { return format(parseISO(doc.created_at), "d MMM", { locale: es }); } catch { return ''; } })() : ''}
                      </p>
                      <div className="flex gap-1 justify-center mt-3">
                        <Button variant="outline" size="sm" onClick={() => handleDownload(doc)}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        {canEdit && (
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(doc.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent>
          <DialogHeader><DialogTitle>Subir Documento</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Archivo *</Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <input type="file" onChange={handleFileSelect} className="hidden" id="file-upload" data-testid="file-input" />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-stone-400" />
                  <p className="text-sm text-stone-500">{selectedFile ? selectedFile.name : 'Haz clic para seleccionar'}</p>
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={uploadForm.name} onChange={e => setUploadForm({ ...uploadForm, name: e.target.value })} placeholder="Nombre del documento" data-testid="doc-name" />
            </div>
            <div className="space-y-2">
              <Label>Categoría</Label>
              <Select value={uploadForm.category} onValueChange={v => setUploadForm({ ...uploadForm, category: v })}>
                <SelectTrigger data-testid="doc-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea value={uploadForm.description} onChange={e => setUploadForm({ ...uploadForm, description: e.target.value })} rows={2} data-testid="doc-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancelar</Button>
            <Button onClick={handleUpload} data-testid="submit-upload-btn">Subir Documento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
